import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../../config';
import { groupStatus } from '../../../../constants/enum';
import type {
  GroupType,
  GroupCreateType,
  GroupEditType,
} from '../../../../validation/group/groupType';

const groupsRef = collection(db, 'groups');

// ユーザーが所属するグループ一覧を取得
export const getGroupsByUser = async (uid: string): Promise<GroupType[]> => {
  const q = query(
    groupsRef,
    where('memberUids', 'array-contains', uid),
    where('status', '==', groupStatus.active),
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs
    .map(doc => doc.data() as GroupType)
    .sort((a, b) => {
      const aTime = a.created_at?.toMillis?.() ?? 0;
      const bTime = b.created_at?.toMillis?.() ?? 0;
      return bTime - aTime;
    });
};

// 全アクティブグループを取得（管理画面用）
export const getAllGroups = async (): Promise<GroupType[]> => {
  const q = query(groupsRef, where('status', '==', groupStatus.active));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs
    .map(doc => doc.data() as GroupType)
    .sort((a, b) => {
      const aTime = a.created_at?.toMillis?.() ?? 0;
      const bTime = b.created_at?.toMillis?.() ?? 0;
      return bTime - aTime;
    });
};

// 単一グループを取得
export const getGroup = async (groupId: string): Promise<GroupType | null> => {
  const docRef = doc(groupsRef, groupId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return docSnap.data() as GroupType;
};

// グループ作成
export const createGroup = async (data: GroupCreateType, userId: string): Promise<string> => {
  const newDocRef = doc(groupsRef);
  const groupData: DocumentData = {
    groupId: newDocRef.id,
    name: data.name,
    description: data.description ?? '',
    memberUids: data.memberUids,
    status: groupStatus.active,
    created_by: userId,
    updated_by: userId,
    created_at: new Date(),
    updated_at: new Date(),
  };
  await setDoc(newDocRef, groupData);
  return newDocRef.id;
};

// グループ更新
export const updateGroup = async (
  groupId: string,
  data: GroupEditType,
  userId: string,
): Promise<void> => {
  const docRef = doc(groupsRef, groupId);
  await updateDoc(docRef, {
    name: data.name,
    description: data.description ?? '',
    memberUids: data.memberUids,
    updated_by: userId,
    updated_at: new Date(),
  });
};

// グループ論理削除
export const deleteGroup = async (groupId: string, userId: string): Promise<void> => {
  const docRef = doc(groupsRef, groupId);
  await updateDoc(docRef, {
    status: groupStatus.deleted,
    updated_by: userId,
    updated_at: new Date(),
  });
};

// グループにメンバー追加
export const addMemberToGroup = async (groupId: string, uid: string): Promise<void> => {
  const docRef = doc(groupsRef, groupId);
  await updateDoc(docRef, {
    memberUids: arrayUnion(uid),
    updated_at: new Date(),
  });
};

// グループからメンバー削除
export const removeMemberFromGroup = async (groupId: string, uid: string): Promise<void> => {
  const docRef = doc(groupsRef, groupId);
  await updateDoc(docRef, {
    memberUids: arrayRemove(uid),
    updated_at: new Date(),
  });
};
